import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// material import
import {
  Autocomplete,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid2,
  TextField,
  Typography,
} from "@mui/material";

// project import
import CustomTable from "../../ui-components/customTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyTwoToneIcon from "@mui/icons-material/ContentCopyTwoTone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PopUpModal from "../../ui-components/PopUpModal";
import DonorDetails from "./donorDetails";
import { todayISO } from "../../utils/dateUtils";
import { useSnackbar } from "../../ui-components/snackbarProvider";
import AdminDBServices from "../../services/v1/adminDBServices";

const ComingWeeksDonations = ({
  donations,
  fetchAllDonations,
  fetchAllInactiveDonors,
}) => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [type, setType] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [packageFilter, setPackageFilter] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const { showSnackbar } = useSnackbar();

  const handleCopyURL = (reservationNumber) => {
    console.log("Reservation Number:", reservationNumber);
    console.log("Filtered Rows Data:", filteredRows);

    const rowData = filteredRows.find(
      (row) =>
        String(row.reservationNumber) === String(reservationNumber) ||
        String(row.reservationID) === String(reservationNumber)
    );

    console.log("Found Row Data:", rowData);

    if (!rowData) {
      showSnackbar("No donor data available", { severity: "error" });
      return;
    }

    const payload = {
      reservation_number: rowData.reservationNumber,
      fullname: rowData.donorName,
      package_title: rowData.package,
      date: rowData.reservedDate,
      package_price: rowData.packageAmount,
      payment_type: rowData.paymentMethod,
      payment_option: rowData.paymentOption,
      donor_email: rowData.email,
      donorID: rowData.donorID,
    };

    console.log("Payload data:", payload);

    const jsonData = JSON.stringify(payload);

    const base64Encoded = btoa(jsonData)
      .replace(/\+/g, "-") // Replace '+' with '-'
      .replace(/\//g, "_") // Replace '/' with '_'
      .replace(/=+$/, ""); // Remove trailing '=' padding

    const encryptedURL = `http://booking.suwaarana.org/order-pay?data=${base64Encoded}`;

    navigator.clipboard.writeText(encryptedURL);

    showSnackbar("Payment URL has been copied", {
      severity: "success",
    });
  };

  const handleEditReservation = (row) => {
    const payload = {
      reservationID: row.reservationID,
      donorID: row.donorID,
      reservation_number: row.reservationNumber,
      fullname: row.donorName,
      contactNo: row.contactNo,
      whatsappNo: row.whatsappNo,
      email: row.email,
      package_title: row.package,
      reservedDate: row.reservedDate,
      package_price: row.packageAmount,
      payment_type: row.paymentMethod,
      payment_option: row.paymentOption,
    };

    const jsonData = JSON.stringify(payload);
    const base64Encoded = btoa(jsonData)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    navigate(`/order-edit?data=${base64Encoded}`);
  };

  const handleSendUpdateLink = async (row) => {
    try {
      const response = await AdminDBServices.sendUpdateLink(row.donorID);
      showSnackbar(response.data?.message || "Update link sent.", {
        severity: "success",
      });
    } catch (err) {
      showSnackbar(err.message || "Failed to send update link.", {
        severity: "error",
      });
    }
  };

  const columns = [
    { label: "No", field: "reservationNumber" },
    {
      label: "Donor Name",
      field: "donorName",
      renderCell: (row) => `${row.title} ${row.donorName}`,
    },
    { label: "Contact No", field: "contactNo" },
    { label: "WhatsApp No", field: "whatsappNo", align: "center" },
    { label: "Booking Date", field: "reservedDate", align: "center" },
    {
      label: "Payment",
      field: "monthlyPaymentStatus",
      renderCell: (row) => (
        <Chip
          label={row.monthlyPaymentStatus}
          color={row.monthlyPaymentStatus === "Success" ? "success" : "warning"}
          variant="contained"
          size="small"
        />
      ),
    },
    { label: "Package", field: "package" },
    {
      field: "moreInfo",
      align: "center",
      renderCell: (row) => (
        <Box display="flex" alignItems="center" gap={3}>
          <VisibilityIcon
            text="View Donor Details"
            color="secondary"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedRow(row);
              setIsModalOpen(true);
            }}
          />
          <EditIcon
            titleAccess="Edit Donor/Reservation Details"
            color="secondary"
            fontSize="small"
            style={{ cursor: "pointer" }}
            onClick={() => handleEditReservation(row)}
          />
          <MailOutlineIcon
            titleAccess="Send Donor an Update Link"
            color="secondary"
            fontSize="small"
            style={{ cursor: "pointer" }}
            onClick={() => handleSendUpdateLink(row)}
          />
          {row.monthlyPaymentStatus === "Pending" && (
            <ContentCopyTwoToneIcon
              color="secondary"
              fontSize="small"
              style={{ cursor: "pointer" }}
              onClick={() => {
                handleCopyURL(row.reservationID);
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  // Filter the rows dynamically based on selected filters
  const filteredRows = donations
    .filter((row) => {
      const today = todayISO();
      const rowDate = row.reservedDate || '';

      const matchFromDate = fromDate ? rowDate >= fromDate : rowDate > today; // Exclude today's data by default
      const matchToDate = toDate ? rowDate <= toDate : true;
      const matchType = type ? row.type === type : true;
      const matchFrequency = frequency ? row.frequency === frequency : true;
      const matchPackage = packageFilter ? row.package === packageFilter : true;

      return (
        matchFromDate &&
        matchToDate &&
        matchType &&
        matchFrequency &&
        matchPackage
      );
    })
    .sort((a, b) => (a.reservedDate || '').localeCompare(b.reservedDate || ''));

  return (
    <Box>
      <Box sx={{ p: 2, border: 1, borderRadius: 2, borderColor: "lightgrey" }}>
        <Box mt={2}>
          <Grid2 container spacing={2} mb={3}>
            {/* From Date */}
            <Grid2 item size={{ xs: 2.4 }}>
              <TextField
                label="From"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid2>
            {/* To Date */}
            <Grid2 item size={{ xs: 2.4 }}>
              <TextField
                label="To"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid2>
            {/* Type */}
            <Grid2 item size={{ xs: 2.4 }}>
              <Autocomplete
                options={["Recurring", "One-Time"]}
                value={type}
                onChange={(event, newValue) => setType(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Type" fullWidth size="small" />
                )}
              />
            </Grid2>
            {/* Frequency */}
            <Grid2 item size={{ xs: 2.4 }}>
              <Autocomplete
                options={["Monthly", "Annually"]}
                value={frequency}
                onChange={(event, newValue) => setFrequency(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Frequency"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid2>
            {/* Package */}
            <Grid2 item size={{ xs: 2.4 }}>
              <Autocomplete
                options={["Sponsor a Day", "Sponsor Lifetime"]}
                value={packageFilter}
                onChange={(event, newValue) => setPackageFilter(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Package"
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Box>
        {donations.length > 0 ? (
          <CustomTable columns={columns} rows={filteredRows} />
        ) : (
          <Typography variant="body1" color="textSecondary">
            No donations available.
          </Typography>
        )}
      </Box>

      <Box>
        <PopUpModal
          open={isModalOpen}
          handleCloseDialog={() => setIsModalOpen(false)}
          title="Donor Details"
          content={
            <DonorDetails
              fetchAllDonations={fetchAllDonations}
              fetchAllInactiveDonors={fetchAllInactiveDonors}
              selectedRow={selectedRow}
              handleCloseDialog={() => setIsModalOpen(false)}
            />
          }
          maxWidth={700}
          showCloseIcon={true}
        />
      </Box>
    </Box>
  );
};

export default ComingWeeksDonations;
